/**
 * FlightCalendar.jsx
 * 機体予約・フライト管理
 * デザイン: DESIGN.md / DESIGN_PRINCIPLES.md 参照
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { flightApi, droneApi, userApi } from '../services/droneOpsApi';
import { naturalSortBy } from '../services/utils';
import { Button, Badge, Alert, Input, Select, Modal, StatusBadge } from '../components/ui';
import { Textarea } from '../components/ui/Input';

import { C } from '../ColorTokens';

// ---- FullCalendar スタイルカスタマイズ ----
const FC_STYLES = `
.flight-calendar-root .fc {
  --fc-border-color: rgba(193, 199, 211, 0.35);
  --fc-today-bg-color: rgba(0, 80, 142, 0.05);
  --fc-neutral-bg-color: rgba(248, 249, 255, 0.4);
  --fc-button-bg-color: #00508e;
  --fc-button-hover-bg-color: #0068b7;
  --fc-button-active-bg-color: #0068b7;
  --fc-button-border-color: #00508e;
  --fc-button-hover-border-color: #0068b7;
  --fc-button-active-border-color: #0068b7;
  --fc-button-text-color: #ffffff;
  font-family: 'Noto Sans JP', sans-serif;
}
.flight-calendar-root .fc-toolbar-title {
  font-family: 'Space Grotesk', sans-serif !important;
  font-size: 20px !important;
  font-weight: 700 !important;
  color: #00508e !important;
  letter-spacing: -0.5px !important;
}
.flight-calendar-root .fc-button {
  border-radius: 9999px !important;
  padding: 5px 16px !important;
  font-family: 'Space Grotesk', sans-serif !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  box-shadow: none !important;
  transition: background 0.15s, transform 0.1s !important;
}
.flight-calendar-root .fc-button:active {
  transform: scale(0.97) !important;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.15) !important;
}
.flight-calendar-root .fc-button-group .fc-button {
  border-radius: 9999px !important;
}
/* 右ツールバー（月・週・日・リスト）- サンプル準拠 pill コンテナ */
.flight-calendar-root .fc-toolbar-chunk:last-child .fc-button-group {
  background: rgba(230, 232, 239, 0.85) !important;
  border-radius: 9999px !important;
  padding: 4px !important;
  gap: 2px !important;
  display: inline-flex !important;
  border: 1px solid rgba(255,255,255,0.4) !important;
  box-shadow: 0 2px 8px rgba(0,104,183,0.07) !important;
  backdrop-filter: blur(8px) !important;
}
.flight-calendar-root .fc-toolbar-chunk:last-child .fc-button-group .fc-button {
  border-radius: 9999px !important;
  background: transparent !important;
  border-color: transparent !important;
  color: #414751 !important;
  box-shadow: none !important;
  padding: 6px 20px !important;
}
.flight-calendar-root .fc-toolbar-chunk:last-child .fc-button-group .fc-button.fc-button-active {
  background: #00508e !important;
  border-color: #00508e !important;
  color: #ffffff !important;
  box-shadow: 0 2px 10px rgba(0,80,142,0.28) !important;
}
.flight-calendar-root .fc-toolbar-chunk:last-child .fc-button-group .fc-button:hover:not(.fc-button-active) {
  background: rgba(255,255,255,0.75) !important;
  color: #00508e !important;
}
.flight-calendar-root .fc-col-header-cell {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: transparent;
  border-color: rgba(193, 199, 211, 0.35);
}
.flight-calendar-root .fc-col-header-cell-cushion {
  color: #414751;
  text-decoration: none !important;
  padding: 8px 4px;
}
.flight-calendar-root .fc-timegrid-slot-label-cushion {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #00508e;
  opacity: 0.65;
}
.flight-calendar-root .fc-timegrid-now-indicator-line {
  border-color: #0068b7;
  border-width: 2px;
}
.flight-calendar-root .fc-timegrid-now-indicator-arrow {
  border-top-color: #0068b7;
}
.flight-calendar-root .fc-scrollgrid {
  border-color: rgba(193, 199, 211, 0.35);
}
.flight-calendar-root .fc-scrollgrid td,
.flight-calendar-root .fc-scrollgrid th {
  border-color: rgba(193, 199, 211, 0.35);
}
.flight-calendar-root .fc-daygrid-day-number {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #00508e;
  text-decoration: none !important;
}
.flight-calendar-root .fc-day-today .fc-daygrid-day-number {
  background: #00508e;
  color: #ffffff;
  border-radius: 9999px;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.flight-calendar-root .fc-list-event:hover td {
  background: rgba(0, 80, 142, 0.04);
}
.flight-calendar-root .fc-list-day-cushion {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  background: rgba(242, 243, 251, 0.8) !important;
}
.flight-calendar-root .fc-event {
  border-radius: 6px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12) !important;
}
.flight-calendar-root .fc-toolbar {
  gap: 8px;
  padding-bottom: 4px;
}
`;

const STATUS_LABELS = {
  planned:   '予約中',
  in_flight: '飛行中',
  completed: '完了',
  cancelled: 'キャンセル',
};

const STATUS_COLORS = {
  planned:   null,
  in_flight: '#10B981',
  completed: '#6B7280',
  cancelled: '#EF4444',
};

const MAINT_TYPE_LABELS = {
  periodic_3m: '3ヶ月定期点検',
  periodic_1m: '1ヶ月定期点検',
  battery:     'バッテリー',
  repair:      '修理',
  pre_flight:  '飛行前点検',
  post_flight: '飛行後点検',
};

// 予約種別（カレンダー色分け用）
const FLIGHT_CATEGORIES = {
  flight:     { label: '飛行',   color: '#0066FF' },
  inspection: { label: '点検',   color: '#FF6600' },
  training:   { label: '訓練',   color: '#9900FF' },
  demo:       { label: 'デモ',   color: '#00CCFF' },
  survey:     { label: '測量',   color: '#00AA00' },
  spray:      { label: '散布',   color: '#CCCC00' },
  other:      { label: 'その他', color: '#666666' },
};

const EMPTY_FORM = {
  thingName:  '',
  groupName:  '',
  pilotId:    '',
  pilotName:  '',
  category:   'flight',
  title:      '',
  startTime:  '',
  endTime:    '',
  location:   '',
  purpose:    '',
  notes:      '',
  altitude:   '',
};

export default function FlightCalendar() {
  const calendarRef = useRef(null);

  const [flights,  setFlights]  = useState([]);
  const [drones,   setDrones]   = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [users,    setUsers]    = useState([]);

  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [formError,      setFormError]      = useState('');
  const [submitting,     setSubmitting]     = useState(false);
  const [currentRange,   setCurrentRange]   = useState(null);
  const [groupConfirm,   setGroupConfirm]   = useState(null);

  const fetchFlights = useCallback(async (from, to) => {
    try {
      const data = await flightApi.list({ from, to });
      setFlights(data.flights ?? []);
    } catch (e) {
      console.error('予約取得エラー:', e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [droneData, userData] = await Promise.all([
          droneApi.list(),
          userApi.list(),
        ]);
        const droneList = droneData.drones ?? [];
        setDrones(naturalSortBy(droneList, 'thingName'));
        setUsers(naturalSortBy(userData.users ?? [], 'name'));

        try {
          const groupData = await droneApi.listGroups();
          setGroups(naturalSortBy(groupData.groups ?? [], 'displayName'));
        } catch {
          const grpSet = new Set();
          droneList.forEach(d => (d.groups ?? []).forEach(g => grpSet.add(g)));
          setGroups([...grpSet].sort((a,b) => a.localeCompare(b, undefined, { numeric: true })).map(g => ({ groupName: g, displayName: g, things: [] })));
        }
      } catch (e) {
        console.error('初期データ取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  }, [currentRange, fetchFlights]);

  const droneMap = Object.fromEntries(drones.map(d => [d.thingName, d]));

  const calendarEvents = flights.map((f) => {
    let color;
    if (f.status === 'cancelled') {
      color = STATUS_COLORS.cancelled;
    } else if (f.status === 'completed') {
      color = STATUS_COLORS.completed;
    } else {
      color = FLIGHT_CATEGORIES[f.category]?.color ?? FLIGHT_CATEGORIES.flight.color;
    }
    return {
      id:              f.flightId,
      title:           f.displayName ?? f.thingName,
      start:           f.startTime,
      end:             f.endTime,
      backgroundColor: color,
      borderColor:     color,
      extendedProps:   f,
    };
  });

  const renderEventContent = (eventInfo) => {
    const f = eventInfo.event.extendedProps;
    const categoryLabel = FLIGHT_CATEGORIES[f.category]?.label ?? '';
    return (
      <div className="fc-event-custom text-white leading-tight p-1" style={{ fontSize: '11px' }}>
        <div className="font-semibold">{f.displayName ?? f.thingName}</div>
        <div>{categoryLabel} / {f.pilotName || '未設定'}</div>
        {f.location && <div className="opacity-80">{f.location}</div>}
      </div>
    );
  };

  const handleDatesSet = (info) => {
    const from = info.startStr.split('T')[0];
    const to   = info.endStr.split('T')[0];
    setCurrentRange({ from, to });
    fetchFlights(from, to);
  };

  const handleDateSelect = (info) => {
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const start = info.allDay ? `${info.startStr}T09:00` : toLocal(info.start);
    const end   = info.allDay ? `${info.startStr}T10:00` : toLocal(info.end);
    setForm({ ...EMPTY_FORM, startTime: start, endTime: end });
    setSelectedFlight(null);
    setFormError('');
    setShowModal(true);
  };

  const handleEventClick = (info) => {
    setSelectedFlight(info.event.extendedProps);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      if (name === 'groupName') next.thingName = '';
      return next;
    });
  };

  const selectedGroupObj = groups.find(g => g.groupName === form.groupName);
  const formDrones = form.groupName
    ? drones.filter(d => (selectedGroupObj?.things ?? d.groups ?? []).includes(d.thingName)
        || (d.groups ?? []).includes(form.groupName))
    : drones;
  const availableDrones = formDrones.filter(d => d.operationalStatus === 'available');

  const handleSubmit = async () => {
    setFormError('');
    if (!form.pilotId)   return setFormError('パイロットを選択してください');
    if (!form.startTime) return setFormError('開始時刻を入力してください');
    if (!form.endTime)   return setFormError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setFormError('終了時刻は開始時刻より後にしてください');

    const isGroupBulk = form.groupName && !form.thingName;
    if (isGroupBulk) {
      const targets = formDrones.filter(d => d.operationalStatus === 'available');
      if (targets.length === 0) return setFormError('グループ内に待機中の機体がありません');
      if (!window.confirm(`グループ「${form.groupName}」の待機中機体 ${targets.length} 台に一括予約します。よろしいですか？`)) return;
    } else if (!form.thingName) {
      return setFormError('機体を選択するか、グループの未選択で一括予約してください');
    }

    setSubmitting(true);
    try {
      if (isGroupBulk) {
        const targets = formDrones.filter(d => d.operationalStatus === 'available');
        const results = await Promise.allSettled(
          targets.map(d => flightApi.create({
            thingName: d.thingName,
            pilotId:   form.pilotId,
            pilotName: form.pilotName,
            title:     form.title,
            startTime: form.startTime,
            endTime:   form.endTime,
            location:  form.location,
            purpose:   form.purpose,
            notes:     form.notes,
            groupName: form.groupName,
            altitude:  form.altitude ? Number(form.altitude) : 0,
            category:  form.category,
          }))
        );
        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          setFormError(`${targets.length - failed.length}件成功、${failed.length}件失敗（時間帯の重複等）`);
        } else {
          setShowModal(false);
        }
      } else {
        await flightApi.create({
          thingName: form.thingName,
          pilotId:   form.pilotId,
          pilotName: form.pilotName,
          title:     form.title,
          startTime: form.startTime,
          endTime:   form.endTime,
          location:  form.location,
          purpose:   form.purpose,
          notes:     form.notes,
          groupName: form.groupName,
          altitude:  form.altitude ? Number(form.altitude) : 0,
          category:  form.category,
        });
        setShowModal(false);
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      setFormError(
        e.message.includes('重複') || e.message.includes('CONFLICT') || e.message.includes('予約')
          ? 'この時間帯はすでに予約されています'
          : e.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約をキャンセルしますか？')) return;
      try {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirm(flight.groupName, 'キャンセル');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime === flight.startTime
        );
        await Promise.allSettled(
          targets.map(f => flightApi.update(f.flightId, { status: 'cancelled' }))
        );
      } else {
        await flightApi.update(flight.flightId, { status: 'cancelled' });
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (flight) => {
    if (!flight.groupName) {
      if (!window.confirm('この予約を削除しますか？')) return;
      try {
        await flightApi.delete(flight.flightId);
        setShowModal(false);
        if (currentRange) fetchFlights(currentRange.from, currentRange.to);
      } catch (e) { alert(e.message); }
      return;
    }
    const choice = await showGroupConfirm(flight.groupName, '削除');
    if (!choice) return;
    try {
      if (choice === 'group') {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName && f.startTime === flight.startTime
        );
        await Promise.allSettled(
          targets.map(f => flightApi.delete(f.flightId))
        );
      } else {
        await flightApi.delete(flight.flightId);
      }
      setShowModal(false);
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) { alert(e.message); }
  };

  const showGroupConfirm = (groupName, action) => {
    return new Promise((resolve) => {
      setGroupConfirm({ groupName, action, resolve });
    });
  };

  const handleEventDrop = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const newStart = toLocal(info.event.start);
    const oldStart = flight.startTime.slice(0, 16);
    const diffMs   = new Date(newStart) - new Date(oldStart);

    const oldDurationMs = new Date(flight.endTime) - new Date(flight.startTime);
    const newEnd = info.event.end
      ? toLocal(info.event.end)
      : toLocal(new Date(new Date(newStart).getTime() + oldDurationMs));

    try {
      if (flight.groupName) {
        const targets = flights.filter(f =>
          f.groupName === flight.groupName &&
          f.startTime.slice(0, 16) === oldStart
        );
        if (targets.length === 0) {
          await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
        } else {
          await Promise.allSettled(
            targets.map(f => {
              const s = toLocal(new Date(new Date(f.startTime).getTime() + diffMs));
              const dur = new Date(f.endTime) - new Date(f.startTime);
              const e = toLocal(new Date(new Date(s).getTime() + dur));
              return flightApi.update(f.flightId, { startTime: s, endTime: e });
            })
          );
        }
      } else {
        await flightApi.update(flight.flightId, { startTime: newStart, endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleEventResize = async (info) => {
    const flight = info.event.extendedProps;
    const toLocal = (date) => {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const newEnd = toLocal(info.event.end);
    try {
      if (flight.groupName) {
        const oldEnd = flight.endTime.slice(0, 16);
        const diffMs = new Date(newEnd) - new Date(oldEnd);
        const targets = flights.filter(f =>
          f.groupName === flight.groupName &&
          f.startTime.slice(0, 16) === flight.startTime.slice(0, 16)
        );
        await Promise.allSettled(
          targets.map(f => {
            const e = toLocal(new Date(new Date(f.endTime).getTime() + diffMs));
            return flightApi.update(f.flightId, { endTime: e });
          })
        );
      } else {
        await flightApi.update(flight.flightId, { endTime: newEnd });
      }
      if (currentRange) fetchFlights(currentRange.from, currentRange.to);
    } catch (e) {
      info.revert();
      alert(e.message);
    }
  };

  const handleUpdate = async (flight, updatedForm, choiceOverride) => {
    const payload = {
      pilotId:   updatedForm.pilotId,
      pilotName: updatedForm.pilotName,
      title:     updatedForm.title,
      startTime: updatedForm.startTime,
      endTime:   updatedForm.endTime,
      location:  updatedForm.location,
      purpose:   updatedForm.purpose,
      notes:     updatedForm.notes,
      altitude:  updatedForm.altitude ? Number(updatedForm.altitude) : 0,
      category:  updatedForm.category,
    };

    let choice = choiceOverride;
    if (flight.groupName && choice === null) {
      choice = await new Promise(resolve => {
        setTimeout(() => {
          setGroupConfirm({ groupName: flight.groupName, action: '変更', resolve });
        }, 0);
      });
      if (!choice) return;
    }

    if (choice === 'single') {
      payload.groupName = '';
      await flightApi.update(flight.flightId, payload);
    } else if (choice === 'group') {
      const targets = flights.filter(f =>
        f.groupName === flight.groupName && f.startTime === flight.startTime
      );
      await Promise.allSettled(
        targets.map(f => flightApi.update(f.flightId, payload))
      );
    } else {
      await flightApi.update(flight.flightId, payload);
    }
    setShowModal(false);
    if (currentRange) fetchFlights(currentRange.from, currentRange.to);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48,
            border: `4px solid ${C.primaryContainer}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 16px',
          }} />
          <p style={{ fontSize: 14, color: C.onSurfaceVariant, fontFamily: "'Noto Sans JP', sans-serif" }}>読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="-m-6 overflow-hidden"
      style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', fontFamily: "'Noto Sans JP', sans-serif" }}
    >
      <style>{FC_STYLES}</style>

      {/* ════════════════════════════════════
          ページヘッダー
      ════════════════════════════════════ */}
      <div style={{ padding: '20px 24px 12px', flexShrink: 0 }}>
        <h1 style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: 28, fontWeight: 700, letterSpacing: '-0.5px',
          color: C.primary, margin: 0, lineHeight: 1.2,
        }}>
          <span
            className="material-symbols-outlined"
            style={{
              fontSize: 28, color: C.primary,
              fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24",
            }}
          >
            calendar_month
          </span>
          機体予約・フライト管理
        </h1>
      </div>

      {/* ════════════════════════════════════
          カレンダーエリア
      ════════════════════════════════════ */}
      <div style={{ flex: 1, overflow: 'hidden', padding: '12px 56px 16px', background: C.surfaceBright }}>
        <div style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255,255,255,0.60)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.22)',
          borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,104,183,0.08)',
          overflow: 'hidden',
        }}>
          {/* FullCalendar 本体 */}
          <div className="flight-calendar-root" style={{ flex: 1, padding: '12px 16px 0', overflow: 'hidden' }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              initialView="timeGridWeek"
              locale={jaLocale}
              headerToolbar={{
                left:   'prev,next today',
                center: 'title',
                right:  'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
              }}
              buttonText={{ today: '今日', month: '月', week: '週', day: '日', list: 'リスト' }}
              height="100%"
              slotMinTime="06:00:00"
              slotMaxTime="21:00:00"
              slotDuration="00:30:00"
              events={calendarEvents}
              selectable={true}
              selectMirror={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              editable={true}
              eventDrop={handleEventDrop}
              eventResize={handleEventResize}
              eventDisplay="block"
              eventContent={renderEventContent}
              nowIndicator={true}
            />
          </div>

          {/* 凡例 */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 16,
            padding: '10px 20px',
            borderTop: '1px solid rgba(193,199,211,0.25)',
            background: 'rgba(248,249,255,0.5)',
            flexShrink: 0,
          }}>
            {Object.entries(FLIGHT_CATEGORIES).map(([key, { label, color }]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  display: 'inline-block',
                  width: 10, height: 10,
                  borderRadius: '50%',
                  backgroundColor: color,
                  flexShrink: 0,
                }} />
                <span style={{
                  fontSize: 11, fontWeight: 600,
                  color: C.onSurfaceVariant,
                  fontFamily: "'Space Grotesk', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* グループ確認モーダル */}
      <Modal
        isOpen={!!groupConfirm}
        onClose={() => { setGroupConfirm(null); groupConfirm?.resolve(null); }}
        title="対象を選択"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setGroupConfirm(null); groupConfirm?.resolve(null); }}>
              閉じる
            </Button>
            <Button variant="secondary" onClick={() => { setGroupConfirm(null); groupConfirm?.resolve('single'); }}>
              この機体のみ
            </Button>
            <Button variant="danger" onClick={() => { setGroupConfirm(null); groupConfirm?.resolve('group'); }}>
              グループ全体
            </Button>
          </>
        }
      >
        {groupConfirm && (
          <div className="space-y-2">
            <p className="text-slate-700">「{groupConfirm.groupName}」グループで登録された予約です。</p>
            <p className="text-slate-700">{groupConfirm.action}する対象を選択してください。</p>
          </div>
        )}
      </Modal>

      {/* 予約ドロワー */}
      <FlightDrawer
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        selectedFlight={selectedFlight}
        droneMap={droneMap}
        form={form}
        groups={groups}
        availableDrones={availableDrones}
        allDrones={formDrones}
        users={users}
        error={formError}
        submitting={submitting}
        onChange={handleFormChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onUpdate={handleUpdate}
      />
    </div>
  );
}

// ---- 予約ドロワー ----
function FlightDrawer({
  isOpen, onClose,
  selectedFlight, droneMap,
  form, groups, availableDrones, allDrones, users,
  error, submitting, onChange, onSubmit,
  onCancel, onDelete, onUpdate,
}) {
  if (!isOpen) return null;
  const isNew = !selectedFlight;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        animation: 'fcOverlayIn 0.4s cubic-bezier(0.4,0,0.2,1) both',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes fcSlideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes fcOverlayIn {
          from { background: rgba(15,23,42,0); backdrop-filter: blur(0px); -webkit-backdrop-filter: blur(0px); }
          to   { background: rgba(15,23,42,0.42); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
        }
      `}</style>

      <div
        style={{
          width: '100%', maxWidth: 640,
          background: 'rgba(248,249,255,0.90)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderRadius: '32px 32px 0 0',
          border: '1px solid rgba(255,255,255,0.32)',
          boxShadow: '0 -20px 60px rgba(0,0,0,0.12)',
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          animation: 'fcSlideUp 0.4s cubic-bezier(0.4,0,0.2,1) both',
          fontFamily: "'Noto Sans JP', sans-serif",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* プルハンドル */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 6px', flexShrink: 0 }}>
          <div style={{ width: 48, height: 4, borderRadius: 9999, background: C.outlineVariant }} />
        </div>

        {/* ドロワーヘッダー */}
        <div style={{
          padding: '4px 28px 14px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
          borderBottom: `1px solid rgba(193,199,211,0.28)`,
        }}>
          <div>
            {/* ラベル */}
            <span style={{
              fontSize: 10, fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600, letterSpacing: '1px',
              color: C.primary, textTransform: 'uppercase',
            }}>
              {isNew ? '予約登録' : '予約詳細'}
            </span>

            {isNew ? (
              <h2 style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: 22, fontWeight: 700,
                color: C.primary, margin: '3px 0 0', letterSpacing: '-0.5px',
              }}>
                新規予約
              </h2>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '3px 0 0' }}>
                  <span style={{
                    width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                    backgroundColor: selectedFlight.droneColor ?? droneMap[selectedFlight.thingName]?.color ?? C.primaryContainer,
                    display: 'inline-block',
                  }} />
                  <h2 style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: 22, fontWeight: 700,
                    color: C.primary, margin: 0, letterSpacing: '-0.5px',
                  }}>
                    {selectedFlight.displayName ?? selectedFlight.thingName}
                  </h2>
                  {selectedFlight.groupName && (
                    <StatusBadge variant="neutral">{selectedFlight.groupName}</StatusBadge>
                  )}
                </div>
                <p style={{ fontSize: 13, color: C.onSurfaceVariant, margin: '4px 0 0', fontFamily: "'Space Grotesk', sans-serif" }}>
                  {selectedFlight.startTime?.slice(0, 16).replace('T', ' ')}
                  {' '}〜{' '}
                  {selectedFlight.endTime?.slice(0, 16).replace('T', ' ')}
                </p>
              </>
            )}
          </div>

          {/* 閉じるボタン */}
          <button
            onClick={onClose}
            style={{
              width: 40, height: 40, borderRadius: '50%', flexShrink: 0, marginTop: 2,
              border: `1px solid ${C.outlineVariant}`,
              background: 'rgba(255,255,255,0.6)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.onSurfaceVariant,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>

        {/* スクロール可能コンテンツ */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px 28px' }}>
          {isNew ? (
            <FlightForm
              form={form}
              groups={groups}
              availableDrones={availableDrones}
              allDrones={allDrones}
              users={users}
              error={error}
              submitting={submitting}
              onChange={onChange}
              onSubmit={onSubmit}
              onClose={onClose}
            />
          ) : (
            <FlightDetail
              flight={selectedFlight}
              droneMap={droneMap}
              users={users}
              onCancel={onCancel}
              onDelete={onDelete}
              onUpdate={onUpdate}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FlightDetail({ flight, droneMap, users, onCancel, onDelete, onUpdate, onClose }) {
  const [form, setForm] = React.useState({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });
  const [error,      setError]      = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const isDirty = JSON.stringify(form) !== JSON.stringify({
    pilotId:   flight.pilotId   ?? '',
    pilotName: flight.pilotName ?? '',
    title:     flight.title     ?? '',
    startTime: flight.startTime ? flight.startTime.slice(0, 16) : '',
    endTime:   flight.endTime   ? flight.endTime.slice(0, 16)   : '',
    location:  flight.location  ?? '',
    purpose:   flight.purpose   ?? '',
    notes:     flight.notes     ?? '',
    altitude:  flight.altitude  ?? '',
    category:  flight.category  ?? 'flight',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'pilotId') {
        const user = users.find(u => u.userId === value);
        next.pilotName = user?.name ?? user?.email ?? '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.pilotId)   return setError('パイロットを選択してください');
    if (!form.startTime) return setError('開始時刻を入力してください');
    if (!form.endTime)   return setError('終了時刻を入力してください');
    if (form.startTime >= form.endTime) return setError('終了時刻は開始時刻より後にしてください');
    setSubmitting(true);
    try {
      await onUpdate(flight, form, flight.groupName ? null : 'single');
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Input
            label="タイトル（任意）"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="例: 現場点検フライト"
          />
        </div>

        <Select
          label="パイロット"
          name="pilotId"
          value={form.pilotId}
          onChange={handleChange}
          options={[
            { value: '', label: '選択してください' },
            ...users.map(u => ({ value: u.userId, label: u.name ?? u.email }))
          ]}
        />

        <div className="leading-normal">
          <label className="block text-sm font-medium text-slate-700 mb-1">ステータス</label>
          <Badge variant={flight.status === 'planned' ? 'primary' : flight.status === 'in_flight' ? 'success' : flight.status === 'cancelled' ? 'danger' : 'default'}>
            {STATUS_LABELS[flight.status] ?? flight.status}
          </Badge>
        </div>

        <Select
          label="予約種別"
          name="category"
          value={form.category}
          onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
          options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({
            value: key,
            label: label
          }))}
        />

        <Input
          label="開始日時"
          type="datetime-local"
          name="startTime"
          value={form.startTime}
          onChange={handleChange}
        />

        <Input
          label="終了日時"
          type="datetime-local"
          name="endTime"
          value={form.endTime}
          onChange={handleChange}
        />

        <Input
          label="場所"
          name="location"
          value={form.location}
          onChange={handleChange}
          placeholder="例: 東京都港区海岸"
        />

        <Input
          label="最大高度 (m)"
          type="number"
          name="altitude"
          value={form.altitude}
          onChange={handleChange}
          placeholder="例: 150"
        />

        <div className="col-span-2">
          <Input
            label="目的"
            name="purpose"
            value={form.purpose}
            onChange={handleChange}
            placeholder="例: 測量・点検・農薬散布"
          />
        </div>

        <div className="col-span-2">
          <Textarea
            label="備考"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={3}
          />
        </div>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-slate-200">
        <div className="flex gap-2">
          {flight.status === 'planned' && (
            <Button variant="danger-outline" onClick={() => onCancel(flight)} disabled={submitting}>
              キャンセル
            </Button>
          )}
          {['planned', 'cancelled'].includes(flight.status) && (
            <Button variant="danger" onClick={() => onDelete(flight)} disabled={submitting}>
              削除
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            閉じる
          </Button>
          {isDirty && (
            <Button onClick={handleSave} loading={submitting}>
              変更を保存
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FlightForm({ form, groups, availableDrones, allDrones, users, error, submitting, onChange, onSubmit, onClose }) {
  const [showDetails, setShowDetails] = React.useState(false);

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="space-y-4">
        {groups.length > 0 && (
          <Select
            label="グループ（一括予約用）"
            name="groupName"
            value={form.groupName}
            onChange={onChange}
            options={[
              { value: '', label: '指定なし' },
              ...groups.map(g => ({ value: g.groupName, label: g.displayName }))
            ]}
          />
        )}

        <Select
          label="機体"
          name="thingName"
          value={form.thingName}
          onChange={onChange}
          options={[
            { value: '', label: '選択してください' },
            ...(availableDrones.length > 0
              ? [
                  { value: '', label: '── 待機中 ──', disabled: true },
                  ...availableDrones.map(d => ({ value: d.thingName, label: d.displayName })),
                  ...(allDrones.filter(d => d.operationalStatus !== 'available').length > 0
                    ? [
                        { value: '', label: '── 点検中・修理中 ──', disabled: true },
                        ...allDrones.filter(d => d.operationalStatus !== 'available').map(d => ({
                          value: d.thingName,
                          label: `${d.displayName}（${d.operationalStatus === 'maintenance' ? '点検中' : '修理中'}）`,
                          disabled: true
                        }))
                      ]
                    : [])
                ]
              : allDrones.map(d => ({ value: d.thingName, label: d.displayName })))
          ]}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            label="開始日時"
            type="datetime-local"
            name="startTime"
            value={form.startTime}
            onChange={onChange}
          />
          <Input
            label="終了日時"
            type="datetime-local"
            name="endTime"
            value={form.endTime}
            onChange={onChange}
          />
        </div>

        <Select
          label="パイロット"
          name="pilotId"
          value={form.pilotId}
          onChange={onChange}
          options={[
            { value: '', label: '選択してください' },
            ...users.map(u => ({ value: u.userId, label: u.name ?? u.email }))
          ]}
        />

        <Select
          label="予約種別"
          name="category"
          value={form.category}
          onChange={onChange}
          options={Object.entries(FLIGHT_CATEGORIES).map(([key, { label }]) => ({
            value: key,
            label: label
          }))}
        />
      </div>

      <div className="border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
        >
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 16 }}
          >
            {showDetails ? 'expand_less' : 'expand_more'}
          </span>
          詳細オプション
        </button>

        {showDetails && (
          <div className="mt-3 space-y-3">
            <Input
              label="タイトル"
              name="title"
              value={form.title}
              onChange={onChange}
              placeholder="例: 現場点検フライト"
            />

            <div className="grid grid-cols-2 gap-3">
              <Input
                label="場所"
                name="location"
                value={form.location}
                onChange={onChange}
                placeholder="例: 東京都港区海岸"
              />
              <Input
                label="最大高度 (m)"
                type="number"
                name="altitude"
                value={form.altitude}
                onChange={onChange}
                placeholder="例: 150"
              />
            </div>

            <Input
              label="目的"
              name="purpose"
              value={form.purpose}
              onChange={onChange}
              placeholder="例: 測量・点検・農薬散布"
            />

            <Textarea
              label="備考"
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows={2}
              placeholder="特記事項があれば入力"
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
        <Button variant="secondary" onClick={onClose}>
          キャンセル
        </Button>
        <Button onClick={onSubmit} loading={submitting}>
          予約登録
        </Button>
      </div>
    </div>
  );
}
